import snakeCase from 'lodash/snakeCase'
import {
  actionNewsletterSchema,
  actionFilterlistingSchema,
  actionFinalizeOrderButtonSchema,
  actionInternalSearchSchema,
  actionReserveInStoreSchema,
  actionChooseCountrySchema,
  actionContanctUsSchema,
  actionNewletterUnsubscribeSchema,
  actionContactCheckoutSchema,
  actionThankYouButtonSchema,
  actionFeedbackSchema,
} from '@integration-layer/schemas/GAEvents/actionEvents'

/**
 * Composable for handling Google Analytics action events across different user interactions.
 * Provides methods to dispatch various tracking events with specific schemas and event details.
 *
 * @returns {Object} An object containing methods for dispatching different action events
 */
export const useGAActionEvents = () => {
  const debug = !!useRuntimeConfig().public.ga4.debug

  /**
   * Dispatches a newsletter subscription event.
   *
   * @param {string} brand - The brand associated with the newsletter subscription
   * @example
   * dispatchActionNewsletterEvent('MyBrand')
   */
  const dispatchActionNewsletterEvent = (brand: string) => {
    useGASendEvent(
      actionNewsletterSchema,
      {
        event: 'GAevent',
        eventID: '004',
        eventCategory: 'newsletter',
        eventAction: 'click_cta',
        eventLabel: 'subscribe',
        eventElement01: snakeCase(brand) ?? '',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Dispatches an event for applying filters in a listing.
   *
   * @param {string} filter - The type of filter applied
   * @param {string} value - The value of the filter
   * @example
   * dispatchActionFilterlistingEvent('color', 'blue')
   */
  const dispatchActionFilterlistingEvent = (filter: string, value: string) => {
    useGASendEvent(
      actionFilterlistingSchema,
      {
        event: 'GAevent',
        eventID: '008',
        eventCategory: 'filter_listing',
        eventAction: 'click_cta',
        eventLabel: filter ?? '',
        eventElement01: value ?? '',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Dispatches an event for finalizing an order.
   *
   * @param {string} currency - The currency of the order
   * @param {number} value - The total value of the order
   * @param {string} shipping - The shipping method
   * @param {string} payment - The payment method
   * @example
   * dispatchActionFinalizeOrderButtonEvent('USD', 100.50, 'Standard', 'Credit Card')
   */
  const dispatchActionFinalizeOrderButtonEvent = (
    currency: string,
    value: number,
    shipping: string,
    payment: string
  ) => {
    useGASendEvent(
      actionFinalizeOrderButtonSchema,
      {
        event: 'GAevent',
        eventID: '009',
        eventCategory: 'finalize_order',
        eventAction: 'click_cta',
        eventLabel: currency ?? '',
        eventElement01: value ?? 0,
        eventElement02: shipping ?? '',
        eventElement03: payment ?? '',
      },
      { debug }
    )
  }

  /**
   * Dispatches an event for internal site search.
   *
   * @param {string} query - The search query entered by the user
   * @example
   * dispatchActionInternalSearchEvent('summer dress')
   */
  const dispatchActionInternalSearchEvent = (query: string) => {
    useGASendEvent(
      actionInternalSearchSchema,
      {
        event: 'GAevent',
        eventID: '013',
        eventCategory: 'internal_search',
        eventAction: 'click_cta',
        eventLabel: query ?? '',
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Dispatches an event for reserving an item in-store.
   *
   * @param {string} itemName - The name of the item
   * @param {string} itemSize - The size of the item
   * @param {string} itemColor - The color of the item
   * @param {string} itemID - The unique identifier of the item
   * @example
   * dispatchActionReserveInStoreEvent('Blue Shirt', 'M', 'Blue', 'SHIRT001')
   */
  const dispatchActionReserveInStoreEvent = (
    itemName: string,
    itemSize: string,
    itemColor: string,
    itemID: string
  ) => {
    useGASendEvent(
      actionReserveInStoreSchema,
      {
        event: 'GAevent',
        eventID: '015',
        eventCategory: 'reserve_in_store',
        eventAction: 'click_cta',
        eventLabel: itemName ?? '',
        eventElement01: itemSize ?? '',
        eventElement02: itemColor ?? '',
        eventElement03: itemID ?? '',
      },
      { debug }
    )
  }

  /**
   * Dispatches an event for choosing a country/language.
   *
   * @param {string} country - The selected country
   * @param {string} language - The selected language
   * @param {'done' | 'start'} action - The stage of country selection
   * @example
   * dispatchActionChooseCountryEvent('US', 'en', 'done')
   */
  const dispatchActionChooseCountryEvent = (
    country: string,
    language: string,
    action: 'done' | 'start'
  ) => {
    useGASendEvent(
      actionChooseCountrySchema,
      {
        event: 'GAevent',
        eventID: '017',
        eventCategory: 'choose_country',
        eventAction: 'click_cta',
        eventLabel: country ?? '',
        eventElement01: language ?? '',
        eventElement02: action,
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Dispatches an event for the contact us page interaction.
   *
   * @example
   * dispatchActionContactUsEvent()
   */
  const dispatchActionContactUsEvent = () => {
    useGASendEvent(
      actionContanctUsSchema,
      {
        event: 'GAevent',
        eventID: '018',
        eventCategory: 'contact_us',
        eventAction: 'click_cta',
        eventLabel: '-',
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Dispatches an event for newsletter unsubscription.
   *
   * @param {string} step - The step of unsubscription
   * @param {string} brand - The brand associated with the newsletter
   * @example
   * dispatchActionNewletterUnsubscribeEvent('confirmation', 'MyBrand')
   */
  const dispatchActionNewletterUnsubscribeEvent = (
    step: string,
    brand: string
  ) => {
    useGASendEvent(
      actionNewletterUnsubscribeSchema,
      {
        event: 'GAevent',
        eventID: '019',
        eventCategory: 'newsletter_unsubscribe',
        eventAction: 'click_cta',
        eventLabel: step ?? '',
        eventElement01: snakeCase(brand) ?? '',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Dispatches an event for contact during checkout.
   *
   * @param {string} step - The checkout step
   * @param {string} channel - The contact channel
   * @example
   * dispatchActionContactCheckoutEvent('shipping', 'email')
   */
  const dispatchActionContactCheckoutEvent = (
    step: string,
    channel: string
  ) => {
    useGASendEvent(
      actionContactCheckoutSchema,
      {
        event: 'GAevent',
        eventID: '024',
        eventCategory: 'contact_checkout',
        eventAction: 'click_cta',
        eventLabel: step ?? '',
        eventElement01: channel ?? '',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Dispatches an event for interactions on the thank you page.
   *
   * @param {string} action - The specific action taken
   * @param {string} transactionID - The unique transaction identifier
   * @param {string} currency - The transaction currency
   * @param {number} transactionValue - The total transaction value
   * @example
   * dispatchActionThankYouButtonEvent('continue_shopping', 'ORDER123', 'USD', 99.99)
   */
  const dispatchActionThankYouButtonEvent = (
    action: string,
    transactionID: string,
    currency: string,
    transactionValue: number
  ) => {
    useGASendEvent(
      actionThankYouButtonSchema,
      {
        event: 'GAevent',
        eventID: '025',
        eventCategory: 'thank_you_page',
        eventAction: 'click_cta',
        eventLabel: action ?? '',
        eventElement01: transactionID ?? '',
        eventElement02: currency ?? '',
        eventElement03: transactionValue ?? '',
      },
      { debug }
    )
  }

  /**
   * Dispatches an event for user feedback on the thank you page.
   *
   * @param {string} rate - The user's rating
   * @param {string} orderID - The order identifier
   * @param {string} currency - The order currency
   * @param {number} orderValue - The total order value
   * @example
   * dispatchActionFeedbackEvent('5', 'ORDER456', 'USD', 150.75)
   */
  const dispatchActionFeedbackEvent = (
    rate: string,
    orderID: string,
    currency: string,
    orderValue: number
  ) => {
    useGASendEvent(
      actionFeedbackSchema,
      {
        event: 'GAevent',
        eventID: '027',
        eventCategory: 'thank_you_page',
        eventAction: 'feedback',
        eventLabel: rate ?? '',
        eventElement01: orderID ?? '',
        eventElement02: currency ?? '',
        eventElement03: orderValue ?? '',
      },
      { debug }
    )
  }

  return {
    dispatchActionNewsletterEvent,
    dispatchActionFilterlistingEvent,
    dispatchActionFinalizeOrderButtonEvent,
    dispatchActionInternalSearchEvent,
    dispatchActionReserveInStoreEvent,
    dispatchActionChooseCountryEvent,
    dispatchActionContactUsEvent,
    dispatchActionNewletterUnsubscribeEvent,
    dispatchActionContactCheckoutEvent,
    dispatchActionThankYouButtonEvent,
    dispatchActionFeedbackEvent,
  }
}
